import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { buildZpl, buildZplBatch, getZplSizeFallback, DEFAULT_LABEL_SIZE, labelSizeLabel } from "../zpl";
import type { CompanyLabelZplTemplates } from "./labelZplTemplates";
import type { LabelPrintJob } from "../types";
import { browserPrint } from "./browserPrint";
import {
  isQzTrayAvailable,
  listQzPrinters,
  printRawZpl,
  qzErrorMessage,
  resolveZebraPrinter,
} from "./qzTray";
import { needsNonZebraConfirmation } from "./printerSelection";
import { confirmNonZebraPrinter } from "./LabelPrinterConfirm";
import {
  getWorkstationLabelPrintingSettings,
  setWorkstationLabelPrintingSettings,
} from "./workstationSettings";

/** Thrown when the user declines the non-Zebra confirmation; callers should not show an error. */
export class PrintCancelledError extends Error {
  constructor() {
    super("Print cancelled");
    this.name = "PrintCancelledError";
  }
}
import { useZebraPrintEnabled } from "./useLabelPrintingSettings";
import { useLabelZplTemplates } from "./useLabelZplTemplates";

/** Warn once per unsupported label kind, only after the print was sent. */
function warnSizeFallbacks(jobs: LabelPrintJob[], overrides: CompanyLabelZplTemplates): void {
  const warnings = new Set<string>();
  for (const job of jobs) {
    const size = getZplSizeFallback(job, overrides);
    if (size) warnings.add(`No ${size} template for ${job.kind} yet — printed the ${labelSizeLabel(DEFAULT_LABEL_SIZE)} layout.`);
  }
  for (const warning of warnings) toast.warning(warning);
}

export type LabelPrinterStatus = "idle" | "connecting" | "printing" | "unavailable" | "error";

export interface UseLabelPrinterResult {
  status: LabelPrinterStatus;
  isZebraAvailable: boolean;
  isZebraEnabled: boolean;
  /** True while company label-printing settings are still loading. */
  isLoading: boolean;
  printZpl: (zpl: string, printerName?: string) => Promise<string>;
  printJob: <K extends LabelPrintJob["kind"]>(
    job: LabelPrintJob<K>,
    printerName?: string,
  ) => Promise<string>;
  printJobs: (jobs: LabelPrintJob[], printerName?: string) => Promise<string>;
  refreshPrinters: () => Promise<string[]>;
  probeQz: () => Promise<boolean>;
  browserPrint: () => void;
  /** @deprecated Prefer `isZebraEnabled`. */
  zebraEnabled: boolean;
  /** @deprecated Prefer `isLoading`. */
  zebraLoading: boolean;
  /** @deprecated Prefer `isZebraAvailable` (null while connecting). */
  qzAvailable: boolean | null;
  /** @deprecated Prefer `printJobs`. */
  zebraPrint: (jobs: LabelPrintJob[]) => Promise<string>;
}

export function useLabelPrinter(): UseLabelPrinterResult {
  const { enabled: zebraEnabled, loading: settingsLoading } = useZebraPrintEnabled();
  const { templates: zplOverrides, loading: templatesLoading } = useLabelZplTemplates();
  const [status, setStatus] = useState<LabelPrinterStatus>("unavailable");
  const [isZebraAvailable, setIsZebraAvailable] = useState(false);
  const [probed, setProbed] = useState(false);

  const loading = settingsLoading || templatesLoading;

  useEffect(() => {
    if (settingsLoading) return;
    if (!zebraEnabled) {
      setIsZebraAvailable(false);
      setStatus("unavailable");
      setProbed(true);
      return;
    }

    let cancelled = false;
    setStatus("connecting");
    setProbed(false);
    isQzTrayAvailable().then((available) => {
      if (cancelled) return;
      setIsZebraAvailable(available);
      setStatus(available ? "idle" : "unavailable");
      setProbed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [zebraEnabled, settingsLoading]);

  const probeQz = useCallback(async () => {
    setStatus("connecting");
    const available = await isQzTrayAvailable();
    setIsZebraAvailable(available);
    setStatus(available ? "idle" : "unavailable");
    setProbed(true);
    return available;
  }, []);

  const printZpl = useCallback(
    async (zpl: string, printerName?: string) => {
      if (!zebraEnabled) {
        throw new Error("Zebra printing is disabled");
      }
      setStatus("printing");
      try {
        const printer = printerName ?? (await resolveZebraPrinter());
        const ws = getWorkstationLabelPrintingSettings();
        if (needsNonZebraConfirmation(printer, ws.confirmedNonZebraPrinters)) {
          const ok = await confirmNonZebraPrinter(printer);
          if (!ok) {
            setStatus("idle");
            throw new PrintCancelledError();
          }
          setWorkstationLabelPrintingSettings({
            confirmedNonZebraPrinters: [...ws.confirmedNonZebraPrinters, printer],
          });
        }
        await printRawZpl(zpl, printer);
        setStatus("idle");
        setIsZebraAvailable(true);
        return printer;
      } catch (err) {
        if (err instanceof PrintCancelledError) throw err;
        setStatus("error");
        throw new Error(qzErrorMessage(err));
      }
    },
    [zebraEnabled],
  );

  const printJob = useCallback(
    async <K extends LabelPrintJob["kind"]>(job: LabelPrintJob<K>, printerName?: string) => {
      const printer = await printZpl(buildZpl(job, zplOverrides), printerName);
      warnSizeFallbacks([job], zplOverrides);
      return printer;
    },
    [printZpl, zplOverrides],
  );

  const printJobs = useCallback(
    async (jobs: LabelPrintJob[], printerName?: string) => {
      const printer = await printZpl(buildZplBatch(jobs, zplOverrides), printerName);
      warnSizeFallbacks(jobs, zplOverrides);
      return printer;
    },
    [printZpl, zplOverrides],
  );

  const refreshPrinters = useCallback(async () => {
    return listQzPrinters();
  }, []);

  const qzAvailable: boolean | null = !probed || status === "connecting" ? null : isZebraAvailable;

  return {
    status,
    isZebraAvailable,
    isZebraEnabled: zebraEnabled,
    isLoading: loading,
    printZpl,
    printJob,
    printJobs,
    refreshPrinters,
    probeQz,
    browserPrint,
    zebraEnabled,
    zebraLoading: loading,
    qzAvailable,
    zebraPrint: printJobs,
  };
}
